import { IAddressEntity } from "@odata/GeneratedEntityTypes";
import { CountryCode } from "@odata/GeneratedEnums";
import { logger } from "@utils/log";

export interface IAddressExtendedEntity extends IAddressEntity {
    AddressCode?: string;
    HouseNumber?: string;
    StreetNumber?: string;
    Region?: string;
}

interface ICsuFeature {
    attributes: {
        kod: string;
        nazev: string;
        popis: string;
    };
}

type TCsuField = keyof ICsuFeature['attributes'];

interface ICsuResponse {
    features: ICsuFeature[];
    exceededTransferLimit: boolean;
    objectIdFieldName: string;
    fields: {
        modelName: string;
        defaultValue: any;
        name: string;
        length: number;
        alias: string;
        type: string;
    }[];
}

export interface IGetAddressArgs {
    country?: CountryCode;
    limit?: number;
    fields?: (keyof IAddressExtendedEntity)[];
}

// CSU API to search and load address places
// return structure: {
// "features":[
//      {"attributes":{"kod":"582786","nazev":"Brno","popis":"Brno (okr. Brno-město)"}}
// ],
// "exceededTransferLimit":false,
// "objectIdFieldName":"objectid",
// "fields": [
//      {"modelName":"kod","defaultValue":null,"name":"kod","length":6,"alias":"kod","type":"esriFieldTypeString"},
//      {"modelName":"nazev","defaultValue":null,"name":"nazev","length":40,"alias":"nazev","type":"esriFieldTypeString"},
//      {"modelName":"popis","defaultValue":null,"name":"popis","length":255,"alias":"popis","type":"esriFieldTypeString"}
// ]}
const getAddressAPIUrl = (q: string, args?: (Omit<IGetAddressArgs, "fields"> & { fields: TCsuField[] })): string => {
    // Fields to be returned from the API, we only need the code and name for now
    const fields: TCsuField[] = ["kod", "nazev", "popis"];
    if (args?.fields) {
        args.fields.forEach(f => {
            if (!fields.includes(f)) {
                fields.push(f);
            }
        });
    }
    q = q.replace(/'/g, "''");
    const condition = encodeURIComponent(`UPPER(nazev) LIKE '%${q.toUpperCase()}%' OR kod LIKE '%${q}%'`);
    const limit = args.limit ?? 10;
    // todo: full addresses (different fields)
    //  https://geodata.csu.gov.cz/server/rest/services/Hosted/Open_data_RSO/FeatureServer/1
    return `https://geodata.csu.gov.cz/server/rest/services/Hosted/CISOB/FeatureServer/48/query?where=${condition}&fullText=&f=json&returnGeometry=false&resultRecordCount=${limit}&outFields=${fields.join(",")}`;
};

const CsuFieldMapping: Partial<Record<keyof IAddressExtendedEntity, TCsuField>> = {
    AddressCode: "kod",
    City: "nazev",
    Region: "popis",
};

function getCsuFieldsFromAddressFields(fields: (keyof IAddressExtendedEntity)[]): TCsuField[] {
    return fields?.map(f => CsuFieldMapping[f]).filter((f): f is TCsuField => !!f);
}

// todo: refactor this to separate method to fetch just city/region codes and full address data.
//  Configuration is not needed probably -> simplify arguments.
export async function searchAddress(query: string, args?: IGetAddressArgs): Promise<IAddressExtendedEntity[]> {
    const { fields: addressFields, country, ...restArgs } = args ?? {};
    const url = getAddressAPIUrl(query, { ...(restArgs ?? {}), fields: getCsuFieldsFromAddressFields(addressFields) });
    try {
        const response = await fetch(url);
        if (!response.ok) {
            logger.error(`Failed to fetch address data: ${response.statusText}`);
            return null;
        }
        const data = (await response.json()) as ICsuResponse;
        const results = data.features ?? [];
        return results.map((feature): IAddressExtendedEntity => {
            const { attributes } = feature;
            return {
                AddressCode: attributes.kod,
                City: attributes.nazev,
                Region: attributes.popis,
                CountryCode: country ?? CountryCode.CzechRepublic,
                HouseNumber: undefined, // API does not provide this info
                StreetNumber: undefined, // API does not provide this info
            };
        });
    } catch (error) {
        logger.error("Error fetching address data:", error);
    }
    return null;
}

// todo: some features from general field defs could be moved here, e.g. address parsing,
//  formatting street address with number from address, general types, etc...
